import React, { useEffect, useState, useMemo, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
    BarChart, Bar, LineChart, Line, AreaChart, Area, ComposedChart, Scatter,
    PieChart, Pie, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    RadialBarChart, RadialBar,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList
} from 'recharts';
import { Spin, Empty, InputNumber, Space, Typography, Button, Dropdown, message } from 'antd';
import { DownloadOutlined, FilePdfOutlined, FileImageOutlined, PictureOutlined, FileTextOutlined } from '@ant-design/icons';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { getGraphViewDataService } from '../../../services/GraphService';
import './graphs.css';
const { Text } = Typography;



// Modern Professional Color Palette
const COLORS = [
    '#25dbcd', // Brand accent
    '#06b6d4', // Cyan 500
    '#ec4899', // Pink 500
    '#10b981', // Emerald 500
    '#f59e0b', // Amber 500
    '#8b5cf6', // Violet 500
    '#3b82f6', // Blue 500
    '#ef4444', // Red 500
    '#14b8a6', // Teal 500
    '#f97316', // Orange 500
    '#a855f7', // Purple 500
    '#d946ef', // Fuchsia 500
    '#f43f5e', // Rose 500
    '#84cc16', // Lime 500
    '#22c55e', // Green 500
    '#0ea5e9', // Sky 500
    '#64748b', // Slate 500
    '#71717a', // Zinc 500
    '#78350f', // Amber 900
    '#831843', // Pink 900
];

// Categorical palette — validated for colorblind separation and surface contrast
// (scripts/validate_palette.js). Fixed order: hues never sit adjacent to a look-alike,
// so charts with a few series stay distinguishable. `end` is a same-hue lighter tint used
// only for the decorative fill gradient — identity stays with `start`.
const GRADIENT_COLORS = [
    { start: '#25DBCD', end: '#5EEAD4' }, // Brand accent
    { start: '#059669', end: '#6EE7B7' }, // Emerald
    { start: '#D97706', end: '#FCD34D' }, // Amber
    { start: '#C026D3', end: '#F0ABFC' }, // Fuchsia
    { start: '#EA580C', end: '#FDBA74' }, // Orange
    { start: '#0891B2', end: '#67E8F9' }, // Cyan
    { start: '#DC2626', end: '#FCA5A5' }, // Red
    { start: '#7C3AED', end: '#C4B5FD' }, // Violet
    { start: '#B45309', end: '#FBBF24' }, // Bronze / dark gold
    { start: '#0D9488', end: '#5EEAD4' }  // Teal
];



// Smart Label function for Pie/Doughnut charts
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, totalItems }) => {
    // Only label slices large enough to hold text cleanly — small slices collide and
    // overlap ("0%", "7%"). Every slice is still fully described in the legend beside it.
    if (percent < 0.08) {
        return null;
    }

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
        <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" style={{ fontSize: '11px', fontWeight: 'bold', textShadow: '0 0 3px rgba(0,0,0,0.5)' }}>
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};

const GraphView = forwardRef(({
    chartType = 'bar',
    data: propData,
    config,
    chartId,
    yAxisColumns = [],
    xAxisLabel = '',
    yAxisLabel = '',
    title = '',
    companyName: propCompanyName = '',
    height = 300,
    showDataLabels = true,
    compact = false,
    columnTitles = {},
    showMetricOverlay = false,
    // Optional date-range override. When `overrideDateRange` is true (e.g. the dashboard's
    // active duration filter), the chart is fetched for exactly this range — even when it is
    // null (meaning "no date filter", e.g. Till Date) — instead of the range saved with the
    // chart at creation time. When false, the saved config dates are used as a fallback.
    dateFrom = null,
    dateTo = null,
    overrideDateRange = false,
    // Whether GraphView renders its own (icon-only) download control. Set false when the
    // parent card already provides a download button, to avoid a duplicate.
    showDownload = true,
    // Reports the resolved metric label (e.g. "SB NO (Count)") up to the parent so it can be
    // shown in the card header instead of a separate row.
    onMetric
}, ref) => {
    const [data, setData] = useState(propData || []);

    // Format column key to readable label using provided titles
    const formatColumnLabel = useCallback((key) => {
        if (!key) return '';

        // Handle potentially pre-formatted keys or simple keys
        const parts = key.split('_');
        if (parts.length >= 2) {
            let agg = parts[parts.length - 1]; // Last part is aggregation
            let dataIndexParts = parts.slice(0, -1);
            
            // Handle percentage_count and percentage_sum which have an underscore
            if (parts.length >= 3 && parts[parts.length - 2] === 'percentage') {
                agg = 'percentage_' + agg;
                dataIndexParts = parts.slice(0, -2);
            }

            // Check if it's a known aggregation
            const knownAggs = ['sum', 'avg', 'count', 'min', 'max', 'distinct', 'percentage_count', 'percentage_sum'];

            if (knownAggs.includes(agg)) {
                const dataIndex = dataIndexParts.join('_');
                const aggLabel = {
                    'sum': 'Sum',
                    'avg': 'Avg',
                    'count': 'Count',
                    'min': 'Min',
                    'max': 'Max',
                    'distinct': 'Distinct',
                    'percentage_count': 'Percentage (Count)',
                    'percentage_sum': 'Percentage (Sum)'
                }[agg] || agg;

                // Use title from map if available
                const colTitle = columnTitles[dataIndex] ||
                    dataIndex.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                return `${colTitle} (${aggLabel})`;
            }
        }

        // Fallback for keys that don't match the pattern or aren't in titles
        return columnTitles[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }, [columnTitles]);
    const [loading, setLoading] = useState(false);
    const token = localStorage.getItem('token');

    // Range selection state for X-axis pagination
    const [rangeStart, setRangeStart] = useState(1);
    const [rangeEnd, setRangeEnd] = useState(16);
    // Temporary state for input fields (before clicking Search)
    const [tempRangeStart, setTempRangeStart] = useState(1);
    const [tempRangeEnd, setTempRangeEnd] = useState(16);

    // Responsive container width tracking
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(500);

    // Resize observer to track container width
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                setContainerWidth(entry.contentRect.width);
            }
        });

        resizeObserver.observe(container);
        // Set initial width
        setContainerWidth(container.offsetWidth);

        return () => resizeObserver.disconnect();
    }, []);

    // Responsive sizing based on container width
    const responsiveStyles = useMemo(() => {
        const isSmall = containerWidth < 350;
        const isMedium = containerWidth >= 350 && containerWidth < 500;

        return {
            // Font sizes
            tickFontSize: isSmall ? 10 : isMedium ? 11 : 12,
            legendFontSize: isSmall ? 12 : isMedium ? 13.5 : 15,
            labelFontSize: isSmall ? 10 : isMedium ? 11 : 12.5,
            // X-axis rotation — gentle angle reads cleaner than a steep diagonal
            xAxisAngle: isSmall ? -45 : isMedium ? -35 : -30,
            xAxisHeight: isSmall ? 52 : isMedium ? 58 : 62,
            // Legend positioning - switch to bottom for small containers
            legendLayout: isSmall ? 'horizontal' : 'vertical',
            legendAlign: isSmall ? 'center' : 'right',
            legendVerticalAlign: isSmall ? 'bottom' : 'middle',
            // Margins
            margins: isSmall
                ? { top: 10, right: 5, left: 0, bottom: 50 }
                : isMedium
                    ? { top: 15, right: 15, left: 5, bottom: 55 }
                    : compact
                        ? { top: 15, right: 15, left: 10, bottom: 35 }
                        : { top: 20, right: 30, left: 60, bottom: 70 },
            // Pie/Doughnut positioning — centered balance to prevent empty spaces
            pieCx: isSmall ? '50%' : '44%',
            pieOuterRadius: isSmall ? '70%' : '80%',
            // Legend max dimensions
            legendMaxHeight: isSmall ? 100 : Math.min(height - 20, 320),
            legendMaxWidth: isSmall ? '100%' : 300,
            // Whether to show legend
            showLegend: containerWidth >= 200,
            // Radial chart positioning
            radialCx: isSmall ? '50%' : '44%',
        };
    }, [containerWidth, compact, height]);

    // Fetch data if config is provided but no data
    useEffect(() => {
        const fetchData = async () => {
            if (config && !propData && chartId) {
                setLoading(true);
                try {
                    // Extract only the fields ChartDataRequestSchema expects.
                    // We must NOT pass the full chart object — extra fields like
                    // chart_id, chart_name, is_active etc. cause a 422 error.
                    const apiPayload = {
                        report_type: config.report_type,
                        x_axis_column: config.x_axis_column,
                        y_axis_columns: config.y_axis_columns,
                        date_column: config.date_column || null,
                        date_aggregation: config.date_aggregation || null,
                        // With an active override use exactly the supplied range (null = no
                        // filter); otherwise fall back to the range saved with the chart.
                        date_from: overrideDateRange ? (dateFrom || null) : (dateFrom || config.filters?.date_from || null),
                        date_to: overrideDateRange ? (dateTo || null) : (dateTo || config.filters?.date_to || null),
                    };
                    const response = await getGraphViewDataService(token, apiPayload);
                    if (response.status === 'success') {
                        setData(response.data || []);
                    }
                } catch (error) {
                    console.error('Error fetching chart data:', error);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchData();
    // Re-fetch when the chart or the active date-range override changes.
    // Use chartId as a stable dep (not config object, which re-creates on every parent render)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartId, propData, token, dateFrom, dateTo, overrideDateRange]);

    // Use prop data if available
    useEffect(() => {
        if (propData) {
            setData(propData);
        }
    }, [propData]);

    // Get Y-axis column keys from yAxisColumns config
    const yColumns = useMemo(() => {
        if (data.length > 0) {
            return Object.keys(data[0]).filter(key => key !== 'name' && key !== '_total');
        }
        if (yAxisColumns && yAxisColumns.length > 0) {
            return yAxisColumns
                .filter(y => y.column)
                .map(y => `${y.column}_${y.aggregation || 'sum'}`);
        }
        if (config?.y_axis_columns) {
            return config.y_axis_columns
                .filter(y => y.column)
                .map(y => `${y.column}_${y.aggregation || 'sum'}`);
        }
        return [];
    }, [yAxisColumns, config, data]);

    // Expose an imperative download() so a parent can trigger the export from its own header.
    // A function-ref decouples this hook from handleDownloadGraph's later definition order.
    const downloadFnRef = useRef(null);
    useImperativeHandle(ref, () => ({
        download: (fmt = 'pdf') => { if (downloadFnRef.current) downloadFnRef.current(fmt); }
    }), []);

    // Report the resolved metric label (circular charts) up to the parent for header display.
    const metricLabel = (['pie', 'doughnut', 'radial'].includes(chartType) && yColumns.length > 0)
        ? formatColumnLabel(yColumns[0])
        : null;
    const onMetricRef = useRef(onMetric);
    onMetricRef.current = onMetric;
    useEffect(() => { if (onMetricRef.current) onMetricRef.current(metricLabel); }, [metricLabel]);

    // Custom tooltip with modern styling
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            // Robustly find title from payload, prioritizing name over label (which might be index)
            let title = payload[0]?.payload?.name || label || '';

            return (
                <div className="chart-tooltip">
                    <p className="chart-tooltip-title">{title}</p>
                    {payload.map((entry, index) => {
                        const isPercentage = entry.name && entry.name.includes('percentage_');
                        return (
                            <p key={index} className="chart-tooltip-item" style={{ color: entry.color }}>
                                {formatColumnLabel(entry.name)}: {typeof entry.value === 'number'
                                    ? entry.value.toLocaleString('en-IN', { maximumFractionDigits: 2 }) + (isPercentage ? '%' : '')
                                    : entry.value}
                            </p>
                        );
                    })}
                </div>
            );

        }
        return null;
    };

    // Format large numbers
    const formatNumber = (value) => {
        if (value >= 10000000) return `${(value / 10000000).toFixed(1)}Cr`;
        if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
        if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
        return value;
    };

    // Calculate total for KPI display
    const totalValue = useMemo(() => {
        if (data.length === 0 || yColumns.length === 0) return 0;
        return data.reduce((sum, item) => sum + (item[yColumns[0]] || 0), 0);
    }, [data, yColumns]);

    // Determine if chart type supports range selection (all chart types support it now)
    const supportsRangeSelection = true;

    // Sliced data for X-axis pagination (only for charts that support it)
    const slicedData = useMemo(() => {
        if (!supportsRangeSelection || data.length <= 15) {
            return data;
        }
        // Convert to 0-indexed for array slicing
        const startIdx = Math.max(0, rangeStart - 1);
        const endIdx = Math.min(data.length, rangeEnd);
        return data.slice(startIdx, endIdx);
    }, [data, rangeStart, rangeEnd, supportsRangeSelection]);

    // Reset range when data changes
    useEffect(() => {
        if (data.length > 0) {
            const initialEnd = Math.min(16, data.length);
            setRangeStart(1);
            setRangeEnd(initialEnd);
            setTempRangeStart(1);
            setTempRangeEnd(initialEnd);
        }
    }, [data.length]);

    // Handle Search button click with validation
    const handleSearchClick = () => {
        // Validate range
        if (tempRangeStart < 1 || tempRangeStart > data.length) {
            message.error(`"From" must be between 1 and ${data.length}`);
            return;
        }
        if (tempRangeEnd < 1 || tempRangeEnd > data.length) {
            message.error(`"To" must be between 1 and ${data.length}`);
            return;
        }
        if (tempRangeEnd < tempRangeStart) {
            message.error('"To" must be greater than or equal to "From"');
            return;
        }
        if (tempRangeEnd - tempRangeStart + 1 > 16) {
            message.error('Range cannot exceed 16 items');
            return;
        }
        // Apply the range
        setRangeStart(tempRangeStart);
        setRangeEnd(tempRangeEnd);
    };

    if (loading) {
        return (
            <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Empty description="No data available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
        );
    }

    // Common axis label style
    const axisLabelStyle = { fontSize: 12, fontWeight: 600, fill: '#64748b' };
    const margins = compact
        ? { top: 30, right: 20, left: 10, bottom: 40 } // Increased top margin to prevent clipping
        : { top: 20, right: 30, left: 60, bottom: 70 };

    // Legend policy for cartesian charts (bar/line/area): a single series needs no legend
    // box — the card title already names it — so we drop the floating legend entirely and
    // let the plot use the full width. Multiple series get a clean legend across the TOP
    // (not floating over the plot on the right).
    const isSingleSeries = (yColumns?.length || 0) <= 1;
    const legendTextSize = Math.max(11, responsiveStyles.legendFontSize - 3);
    const cartesianLegend = (!isSingleSeries && responsiveStyles.showLegend) ? (
        <Legend
            layout="horizontal"
            verticalAlign="top"
            align="center"
            iconType="circle"
            iconSize={9}
            formatter={(value) => (
                <span style={{ fontSize: legendTextSize, color: '#475569', fontWeight: 600 }}>
                    {formatColumnLabel(value)}
                </span>
            )}
            wrapperStyle={{ paddingBottom: 18 }}
        />
    ) : null;

    const renderBarChart = () => (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart data={slicedData} margin={responsiveStyles.margins}>
                <defs>
                    {yColumns.map((col, index) => (
                        <linearGradient key={col} id={`gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].start} stopOpacity={1} />
                            <stop offset="55%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].start} stopOpacity={0.92} />
                            <stop offset="100%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].end} stopOpacity={0.7} />
                        </linearGradient>
                    ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                    dataKey="name"
                    tick={{ fontSize: responsiveStyles.tickFontSize, fill: '#64748b' }}
                    angle={responsiveStyles.xAxisAngle}
                    textAnchor="end"
                    height={responsiveStyles.xAxisHeight}
                    label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5, style: { fontSize: responsiveStyles.labelFontSize, fontWeight: 600, fill: '#64748b' } } : null}
                />
                <YAxis
                    tick={{ fontSize: responsiveStyles.tickFontSize, fill: '#64748b' }}
                    tickFormatter={formatNumber}
                    label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: responsiveStyles.labelFontSize, fontWeight: 600, fill: '#64748b' } } : null}
                />
                <Tooltip content={<CustomTooltip />} />
                {cartesianLegend}
                {yColumns.map((col, index) => (
                    <Bar
                        key={col}
                        dataKey={col}
                        name={col}
                        fill={`url(#gradient-${index})`}
                        radius={[8, 8, 0, 0]}
                        maxBarSize={64}
                        minPointSize={3}
                        animationDuration={800}
                    >
                        {showDataLabels && (
                            <LabelList
                                dataKey={col}
                                position="top"
                                formatter={formatNumber}
                                style={{ fill: '#334155', fontSize: 11, fontWeight: 700 }}
                            />
                        )}
                    </Bar>
                ))}
            </BarChart>
        </ResponsiveContainer>
    );

    const renderLineChart = () => (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart data={slicedData} margin={responsiveStyles.margins}>
                <defs>
                    {yColumns.map((col, index) => (
                        <linearGradient key={col} id={`line-stroke-${index}`} x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].start} />
                            <stop offset="100%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].end} />
                        </linearGradient>
                    ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                    dataKey="name"
                    tick={{ fontSize: responsiveStyles.tickFontSize, fill: '#64748b' }}
                    angle={responsiveStyles.xAxisAngle}
                    textAnchor="end"
                    height={responsiveStyles.xAxisHeight}
                    label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5, style: { fontSize: responsiveStyles.labelFontSize, fontWeight: 600, fill: '#64748b' } } : null}
                />
                <YAxis
                    tick={{ fontSize: responsiveStyles.tickFontSize, fill: '#64748b' }}
                    tickFormatter={formatNumber}
                    label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: responsiveStyles.labelFontSize, fontWeight: 600, fill: '#64748b' } } : null}
                />
                <Tooltip content={<CustomTooltip />} />
                {cartesianLegend}
                {yColumns.map((col, index) => (
                    <Line
                        key={col}
                        type="monotone"
                        dataKey={col}
                        name={col}
                        stroke={`url(#line-stroke-${index})`}
                        strokeWidth={3}
                        strokeLinecap="round"
                        dot={{ fill: GRADIENT_COLORS[index % GRADIENT_COLORS.length].start, strokeWidth: 2, stroke: '#fff', r: 4 }}
                        activeDot={{ r: 7, strokeWidth: 2, stroke: '#fff', fill: GRADIENT_COLORS[index % GRADIENT_COLORS.length].start }}
                        animationDuration={800}
                    >
                        {showDataLabels && (
                            <LabelList
                                dataKey={col}
                                position="top"
                                formatter={formatNumber}
                                style={{ fill: '#334155', fontSize: 11, fontWeight: 700 }}
                            />
                        )}
                    </Line>
                ))}
            </LineChart>
        </ResponsiveContainer>
    );

    const renderAreaChart = () => (
        <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={slicedData} margin={responsiveStyles.margins}>
                <defs>
                    {yColumns.map((col, index) => (
                        <linearGradient key={col} id={`area-gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].start} stopOpacity={0.45} />
                            <stop offset="95%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].end} stopOpacity={0.04} />
                        </linearGradient>
                    ))}
                    {yColumns.map((col, index) => (
                        <linearGradient key={`s-${col}`} id={`area-stroke-${index}`} x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].start} />
                            <stop offset="100%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].end} />
                        </linearGradient>
                    ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                    dataKey="name"
                    tick={{ fontSize: responsiveStyles.tickFontSize, fill: '#64748b' }}
                    angle={responsiveStyles.xAxisAngle}
                    textAnchor="end"
                    height={responsiveStyles.xAxisHeight}
                    label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5, style: { fontSize: responsiveStyles.labelFontSize, fontWeight: 600, fill: '#64748b' } } : null}
                />
                <YAxis
                    tick={{ fontSize: responsiveStyles.tickFontSize, fill: '#64748b' }}
                    tickFormatter={formatNumber}
                    label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: responsiveStyles.labelFontSize, fontWeight: 600, fill: '#64748b' } } : null}
                />
                <Tooltip content={<CustomTooltip />} />
                {cartesianLegend}
                {yColumns.map((col, index) => (
                    <Area
                        key={col}
                        type="monotone"
                        dataKey={col}
                        name={col}
                        stroke={`url(#area-stroke-${index})`}
                        fill={`url(#area-gradient-${index})`}
                        strokeWidth={2.5}
                        strokeLinecap="round"
                        dot={false}
                        activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff', fill: GRADIENT_COLORS[index % GRADIENT_COLORS.length].start }}
                        animationDuration={800}
                    >
                        {showDataLabels && (
                            <LabelList
                                dataKey={col}
                                position="top"
                                formatter={formatNumber}
                                style={{ fill: '#334155', fontSize: 11, fontWeight: 700 }}
                            />
                        )}
                    </Area>
                ))}
            </AreaChart>
        </ResponsiveContainer>
    );

    const renderPieLegendText = (value, entry) => {
        const itemVal = entry?.payload?.value ?? entry?.value;
        const isNum = typeof itemVal === 'number' && !isNaN(itemVal);
        const pieSum = slicedData.reduce((acc, r) => {
            const v = r.value !== undefined ? r.value : r[yColumns[0]];
            return acc + (Number(v) || 0);
        }, 0);
        const pct = (isNum && pieSum > 0) ? ((itemVal / pieSum) * 100).toFixed(0) : 0;
        const formattedVal = isNum ? formatNumber(itemVal) : '';

        const fs = responsiveStyles.legendFontSize;
        // Distribute the legend rows down the legend's available height so the column balances
        // the pie without overflowing (which caused a scrollbar). Padding is derived from the
        // space left inside the legend box (with a buffer) after the rows, then clamped.
        const itemCount = Math.max((slicedData?.length || 1), 1);
        const rowH = 36; // approx height of a two-line item
        const legendH = (typeof responsiveStyles.legendMaxHeight === 'number' ? responsiveStyles.legendMaxHeight : height);
        const vPad = Math.max(2, Math.min(18, Math.floor((legendH - 16 - itemCount * rowH) / (itemCount * 2))));
        return (
            <span
                title={value}
                style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    verticalAlign: 'middle',
                    paddingLeft: 4,
                    paddingTop: vPad,
                    paddingBottom: vPad,
                    lineHeight: 1.25,
                    maxWidth: 200,
                }}
            >
                <span style={{
                    color: '#0f172a', fontWeight: 600, fontSize: fs - 2.5,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                }}>{value}</span>
                {isNum && (
                    <span style={{ fontSize: fs - 3.5, color: '#64748b', fontWeight: 500, whiteSpace: 'nowrap' }}>
                        <strong style={{ color: '#334155', fontWeight: 700 }}>{formattedVal}</strong>
                        <span style={{ color: '#25DBCD', fontWeight: 700, marginLeft: 6 }}>({pct}%)</span>
                    </span>
                )}
            </span>
        );
    };

    const renderPieChart = () => {
        const pieData = slicedData.map((item, index) => ({
            name: item.name,
            value: item[yColumns[0]] || 0,
            fill: `url(#pie-grad-${index})`
        }));

        return (
            <ResponsiveContainer width="100%" height={height}>
                <PieChart>
                    <defs>
                        {slicedData.map((_, index) => (
                            <linearGradient key={index} id={`pie-grad-${index}`} x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].start} />
                                <stop offset="100%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].end} />
                            </linearGradient>
                        ))}
                    </defs>
                    <Pie
                        data={pieData}
                        cx={responsiveStyles.pieCx}
                        cy="50%"
                        innerRadius={0}
                        outerRadius={responsiveStyles.pieOuterRadius}
                        paddingAngle={1.5}
                        minAngle={2}
                        dataKey="value"
                        labelLine={false}
                        label={(props) => renderCustomizedLabel({ ...props, totalItems: pieData.length })}
                        animationDuration={800}
                        stroke="#ffffff"
                        strokeWidth={2}
                    >
                        {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    {responsiveStyles.showLegend && (
                        <Legend
                            layout={responsiveStyles.legendLayout}
                            verticalAlign={responsiveStyles.legendVerticalAlign}
                            align={responsiveStyles.legendAlign}
                            formatter={renderPieLegendText}
                            iconType="circle"
                            wrapperStyle={{
                                maxHeight: responsiveStyles.legendMaxHeight,
                                maxWidth: responsiveStyles.legendMaxWidth,
                                overflowY: 'auto',
                                overflowX: 'auto',
                                paddingLeft: responsiveStyles.legendLayout === 'vertical' ? 12 : 0,
                                fontSize: responsiveStyles.legendFontSize
                            }}
                        />
                    )}
                </PieChart>
            </ResponsiveContainer>
        );
    };

    const renderDoughnutChart = () => {
        const pieData = slicedData.map((item, index) => ({
            name: item.name,
            value: item[yColumns[0]] || 0,
            fill: `url(#doughnut-grad-${index})`
        }));

        return (
            <ResponsiveContainer width="100%" height={height}>
                <PieChart>
                    <defs>
                        {slicedData.map((_, index) => (
                            <linearGradient key={index} id={`doughnut-grad-${index}`} x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].start} />
                                <stop offset="100%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].end} />
                            </linearGradient>
                        ))}
                    </defs>
                    <Pie
                        data={pieData}
                        cx={responsiveStyles.pieCx}
                        cy="50%"
                        innerRadius="46%"
                        outerRadius={responsiveStyles.pieOuterRadius}
                        paddingAngle={2}
                        minAngle={2}
                        dataKey="value"
                        labelLine={false}
                        label={(props) => renderCustomizedLabel({ ...props, totalItems: pieData.length })}
                        animationDuration={800}
                        stroke="#ffffff"
                        strokeWidth={2}
                    >
                        {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                    </Pie>
                    <text x={responsiveStyles.pieCx} y="47%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '18px', fontWeight: '800', fill: '#0f172a' }}>
                        {formatNumber(totalValue)}
                    </text>
                    <text x={responsiveStyles.pieCx} y="55%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '10px', fontWeight: '700', fill: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Total
                    </text>
                    <Tooltip content={<CustomTooltip />} />
                    {responsiveStyles.showLegend && (
                        <Legend
                            layout={responsiveStyles.legendLayout}
                            verticalAlign={responsiveStyles.legendVerticalAlign}
                            align={responsiveStyles.legendAlign}
                            formatter={renderPieLegendText}
                            iconType="circle"
                            wrapperStyle={{
                                maxHeight: responsiveStyles.legendMaxHeight,
                                maxWidth: responsiveStyles.legendMaxWidth,
                                overflowY: 'auto',
                                overflowX: 'auto',
                                paddingLeft: responsiveStyles.legendLayout === 'vertical' ? 12 : 0,
                                fontSize: responsiveStyles.legendFontSize
                            }}
                        />
                    )}
                </PieChart>
            </ResponsiveContainer>
        );
    };

    const renderRadarChart = () => (
        <ResponsiveContainer width="100%" height={height}>
            <RadarChart cx="50%" cy="50%" outerRadius="60%" data={slicedData}>
                <defs>
                    {yColumns.map((col, index) => (
                        <linearGradient key={col} id={`radar-grad-${index}`} x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].start} stopOpacity={0.8} />
                            <stop offset="100%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].end} stopOpacity={0.3} />
                        </linearGradient>
                    ))}
                </defs>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="name" tick={{ fontSize: responsiveStyles.tickFontSize, fill: '#64748b' }} />
                <PolarRadiusAxis tick={{ fontSize: responsiveStyles.tickFontSize - 1, fill: '#64748b', dy: 8 }} />
                {yColumns.map((col, index) => (
                    <Radar
                        key={col}
                        name={formatColumnLabel(col)}
                        dataKey={col}
                        stroke={GRADIENT_COLORS[index % GRADIENT_COLORS.length].start}
                        fill={`url(#radar-grad-${index})`}
                        fillOpacity={0.6}
                        strokeWidth={2}
                        animationDuration={800}
                    />
                ))}
                <Tooltip content={<CustomTooltip />} />
                {responsiveStyles.showLegend && (
                    <Legend
                        iconType="circle"
                        wrapperStyle={{ fontSize: responsiveStyles.legendFontSize }}
                    />
                )}
            </RadarChart>
        </ResponsiveContainer>
    );

    const renderComposedChart = () => (
        <ResponsiveContainer width="100%" height={height}>
            <ComposedChart data={slicedData} margin={margins}>
                <defs>
                    {yColumns.map((col, index) => (
                        <linearGradient key={col} id={`composed-grad-${index}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].start} stopOpacity={1} />
                            <stop offset="100%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].end} stopOpacity={0.7} />
                        </linearGradient>
                    ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5, style: axisLabelStyle } : null}
                />
                <YAxis
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickFormatter={formatNumber}
                    label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', style: axisLabelStyle } : null}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                    layout="vertical"
                    verticalAlign="middle"
                    align="right"
                    formatter={(value) => formatColumnLabel(value)}
                    wrapperStyle={{ paddingLeft: '20px' }}
                />
                {yColumns.map((col, index) => {
                    if (index === 0) {
                        return (
                            <Bar key={col} dataKey={col} name={col} fill={`url(#composed-grad-${index})`} barSize={20} radius={[4, 4, 0, 0]} />
                        );
                    } else {
                        return (
                            <Line key={col} type="monotone" dataKey={col} name={col} stroke={GRADIENT_COLORS[index % GRADIENT_COLORS.length].start} strokeWidth={3} dot={{ r: 4 }} />
                        );
                    }
                })}
            </ComposedChart>
        </ResponsiveContainer>
    );

    const renderRadialBarChart = () => {
        const radialData = slicedData.map((item, index) => ({
            name: item.name,
            [yColumns[0]]: item[yColumns[0]] || 0,
            fill: `url(#radial-grad-${index})`
        }));

        const ringCount = radialData.length || 1;
        const chartSize = Math.min(containerWidth, height);
        const padding = 20;
        const maxRadius = (chartSize / 2) - padding;
        const gap = 4;
        const barSize = Math.max(6, Math.floor((maxRadius - 20) / ringCount) - gap);
        const innerRadius = 20;
        const outerRadius = innerRadius + ringCount * (barSize + gap);

        return (
            <ResponsiveContainer width="100%" height={height}>
                <RadialBarChart
                    cx={responsiveStyles.radialCx}
                    cy="50%"
                    innerRadius={innerRadius}
                    outerRadius={outerRadius}
                    barSize={barSize}
                    data={radialData}
                    startAngle={90}
                    endAngle={-270}
                >
                    <defs>
                        {slicedData.map((_, index) => (
                            <linearGradient key={index} id={`radial-grad-${index}`} x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].start} />
                                <stop offset="100%" stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].end} />
                            </linearGradient>
                        ))}
                    </defs>
                    <RadialBar
                        dataKey={yColumns[0]}
                        clockWise
                        cornerRadius={barSize / 2}
                        background={true}
                        label={{ position: 'insideStart', fill: '#ffffff', fontSize: responsiveStyles.labelFontSize, fontWeight: 'bold' }}
                    />
                    {responsiveStyles.showLegend && (
                        <Legend
                            iconSize={10}
                            layout={responsiveStyles.legendLayout}
                            verticalAlign={responsiveStyles.legendVerticalAlign}
                            iconType="circle"
                            align={responsiveStyles.legendAlign}
                            formatter={renderPieLegendText}
                            wrapperStyle={{
                                maxHeight: responsiveStyles.legendMaxHeight,
                                maxWidth: responsiveStyles.legendMaxWidth,
                                overflowY: 'auto',
                                overflowX: 'auto',
                                paddingLeft: 10,
                                fontSize: responsiveStyles.legendFontSize
                            }}
                        />
                    )}
                    <Tooltip content={<CustomTooltip />} />
                </RadialBarChart>
            </ResponsiveContainer>
        );
    };


    const handleDownloadGraph = async (format = 'pdf') => {
        const graphTitle = title || config?.chart_name || 'Graph Report';
        const cleanTitle = graphTitle.replace(/[^a-zA-Z0-9_-]/g, '_');

        if (format === 'csv') {
            const exportData = slicedData && slicedData.length > 0 ? slicedData : data;
            if (!exportData || exportData.length === 0) {
                message.error('No data available to download');
                return;
            }
            const headers = Object.keys(exportData[0]).filter(k => k !== 'fill' && k !== 'color' && k !== '_total');
            const csvRows = [
                headers.map(h => formatColumnLabel(h)).join(','),
                ...exportData.map(row => headers.map(h => JSON.stringify(row[h] ?? '')).join(','))
            ];
            const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${cleanTitle}_data.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            message.success('Graph data downloaded as CSV!');
            return;
        }

        if (!containerRef.current) {
            message.error('Graph element not found');
            return;
        }

        const btnEl = containerRef.current.querySelector('.graph-download-btn-wrap');

        try {
            message.loading({ content: 'Generating PDF document...', key: 'download_msg', duration: 1 });
            if (btnEl) btnEl.style.visibility = 'hidden';

            const rawCanvas = await html2canvas(containerRef.current, {
                scale: 2,
                backgroundColor: '#ffffff',
                useCORS: true,
                logging: false,
                allowTaint: true
            });

            if (btnEl) btnEl.style.visibility = 'visible';

            // Auto-trim empty vertical white space from canvas
            const ctx = rawCanvas.getContext('2d');
            const w = rawCanvas.width;
            const h = rawCanvas.height;
            const imgPixels = ctx.getImageData(0, 0, w, h);
            const dataArr = imgPixels.data;

            let topPixel = h;
            let bottomPixel = 0;

            for (let y = 0; y < h; y++) {
                for (let x = 0; x < w; x++) {
                    const idx = (y * w + x) * 4;
                    const r = dataArr[idx];
                    const g = dataArr[idx + 1];
                    const b = dataArr[idx + 2];
                    const a = dataArr[idx + 3];

                    if (a > 20 && (r < 248 || g < 248 || b < 248)) {
                        if (y < topPixel) topPixel = y;
                        if (y > bottomPixel) bottomPixel = y;
                    }
                }
            }

            if (topPixel >= bottomPixel) {
                topPixel = 0;
                bottomPixel = h;
            }

            topPixel = Math.max(0, topPixel - 20);
            bottomPixel = Math.min(h, bottomPixel + 20);
            const trimmedH = bottomPixel - topPixel;

            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = trimmedH;
            const trimmedCtx = canvas.getContext('2d');
            trimmedCtx.fillStyle = '#ffffff';
            trimmedCtx.fillRect(0, 0, w, trimmedH);
            trimmedCtx.drawImage(rawCanvas, 0, topPixel, w, trimmedH, 0, 0, w, trimmedH);

            const imgData = canvas.toDataURL('image/png');
            const canvasWidth = canvas.width / 2;
            const canvasHeight = canvas.height / 2;

            if (format === 'svg') {
                const svgEl = containerRef.current.querySelector('svg');
                if (svgEl) {
                    const svgClone = svgEl.cloneNode(true);
                    if (!svgClone.getAttribute('xmlns')) svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
                    const svgString = new XMLSerializer().serializeToString(svgClone);
                    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${cleanTitle}.svg`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    message.success('Graph downloaded as SVG!');
                    return;
                }
            }

            if (format === 'pdf') {
                const companyName = propCompanyName || localStorage.getItem('name') || localStorage.getItem('company_name') || localStorage.getItem('company') || 'Cummins Generator Technologies India Private Limited';
                const iecNo = localStorage.getItem('iec_no') || localStorage.getItem('iec') || '';

                // Target PDF width
                const pdfWidth = 560;
                
                // Moderate, balanced graph size
                const maxGraphWidth = 400;
                const maxGraphHeight = 240;
                const imgRatio = canvas.width / canvas.height;

                let renderWidth = maxGraphWidth;
                let renderHeight = renderWidth / imgRatio;

                if (renderHeight > maxGraphHeight) {
                    renderHeight = maxGraphHeight;
                    renderWidth = maxGraphHeight * imgRatio;
                }

                const headerHeight = 40;
                const tableRows = (slicedData && slicedData.length > 0 ? slicedData : data)?.slice(0, 8) || [];
                const legendHeight = tableRows.length > 0 ? (36 + tableRows.length * 22) : 0;

                const pdfHeight = headerHeight + renderHeight + legendHeight + 25;

                const pdf = new jsPDF({
                    orientation: pdfWidth >= pdfHeight ? 'landscape' : 'portrait',
                    unit: 'px',
                    format: [pdfWidth, pdfHeight]
                });

                // White Background
                pdf.setFillColor(255, 255, 255);
                pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');

                // 1. Header Banner
                pdf.setFillColor(248, 250, 252);
                pdf.rect(0, 0, pdfWidth, 36, 'F');
                pdf.setDrawColor(226, 232, 240);
                pdf.line(0, 36, pdfWidth, 36);

                // Company Name (Left)
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(12);
                pdf.setTextColor(15, 23, 42);
                pdf.text(companyName, 18, 23);

                // IEC & Date (Right)
                const dateStr = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(9);
                pdf.setTextColor(100, 116, 139);
                pdf.text(`IEC: ${iecNo || 'N/A'} • ${dateStr}`, pdfWidth - 18, 23, { align: 'right' });

                // 2. Centered Moderate-Sized Graph Image
                const imgX = (pdfWidth - renderWidth) / 2;
                const imgY = 44;
                pdf.addImage(imgData, 'PNG', imgX, imgY, renderWidth, renderHeight);

                // 3. Large & Prominent Data Legend Breakdown Table
                if (tableRows.length > 0) {
                    let currentY = imgY + renderHeight + 14;

                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(11);
                    pdf.setTextColor(30, 41, 59);
                    pdf.text('Data Legend & Breakdown:', 18, currentY);
                    currentY += 8;

                    // Table Header (22px height)
                    pdf.setFillColor(241, 245, 249);
                    pdf.rect(18, currentY, pdfWidth - 36, 22, 'F');
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(10.5);
                    pdf.setTextColor(71, 85, 105);
                    pdf.text('Category / Series', 28, currentY + 15);
                    pdf.text('Value', pdfWidth - 28, currentY + 15, { align: 'right' });
                    currentY += 22;

                    // Table Rows (22px height each, 10.5pt font)
                    pdf.setFont('helvetica', 'normal');
                    pdf.setFontSize(10.5);
                    tableRows.forEach((row, idx) => {
                        const catName = String(row.name || row.category || `Item ${idx + 1}`).substring(0, 55);
                        const val = row[yColumns[0]] !== undefined ? row[yColumns[0]] : (row.value !== undefined ? row.value : '');
                        const formattedVal = typeof val === 'number' ? val.toLocaleString('en-IN') : String(val);

                        if (idx % 2 === 1) {
                            pdf.setFillColor(248, 250, 252);
                            pdf.rect(18, currentY, pdfWidth - 36, 22, 'F');
                        }
                        pdf.setTextColor(51, 65, 85);
                        pdf.text(catName, 28, currentY + 15);
                        pdf.text(formattedVal, pdfWidth - 28, currentY + 15, { align: 'right' });
                        currentY += 22;
                    });
                }

                pdf.save(`${cleanTitle}.pdf`);
                message.success('Graph downloaded as PDF!');
                return;
            }

            // PNG Download
            const link = document.createElement('a');
            link.href = imgData;
            link.download = `${cleanTitle}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            message.success('Graph downloaded as PNG!');
        } catch (err) {
            console.error('Error exporting graph:', err);
            if (btnEl) btnEl.style.visibility = 'visible';
            message.error('Failed to export graph document');
        }
    };

    // Keep the imperative download() (exposed above) pointing at the current handler.
    downloadFnRef.current = handleDownloadGraph;

    const downloadMenuItems = [
        {
            key: 'pdf',
            label: 'Download PDF Document',
            icon: <FilePdfOutlined style={{ color: '#ef4444' }} />,
            onClick: () => handleDownloadGraph('pdf')
        },
        {
            key: 'png',
            label: 'Download PNG Image',
            icon: <FileImageOutlined style={{ color: '#25dbcd' }} />,
            onClick: () => handleDownloadGraph('png')
        },
        {
            key: 'svg',
            label: 'Download SVG Vector',
            icon: <PictureOutlined style={{ color: '#06b6d4' }} />,
            onClick: () => handleDownloadGraph('svg')
        },
        {
            key: 'csv',
            label: 'Download CSV Data',
            icon: <FileTextOutlined style={{ color: '#10b981' }} />,
            onClick: () => handleDownloadGraph('csv')
        }
    ];

    const chartRenderers = {
        bar: renderBarChart,
        line: renderLineChart,
        area: renderAreaChart,
        pie: renderPieChart,
        doughnut: renderDoughnutChart,
        radar: renderRadarChart,
        composed: renderComposedChart,
        radial: renderRadialBarChart
    };

    const renderChart = chartRenderers[chartType] || renderBarChart;

    const showMetricBadge = showMetricOverlay && ['pie', 'doughnut', 'radial'].includes(chartType) && yColumns.length > 0;
    const showTopBar = showDownload || showMetricBadge;

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
            {showTopBar && (
            <div className="graph-download-btn-wrap" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '8px',
                marginBottom: '4px',
                minHeight: '30px',
                flexShrink: 0
            }}>
                {/* Y-Axis Info for circular charts */}
                {showMetricBadge && (
                    <span style={{
                        background: 'rgba(248, 250, 252, 0.95)',
                        padding: '2px 8px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        color: '#64748b',
                        border: '1px solid #e2e8f0',
                        fontWeight: 500,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                    }}>
                        <span style={{ fontWeight: 600, color: '#475569' }}>Metric: </span>
                        {formatColumnLabel(yColumns[0])}
                    </span>
                )}

                {showDownload && (
                    <Dropdown menu={{ items: downloadMenuItems }} placement="bottomRight" trigger={['contextMenu']}>
                        <Button
                            size="small"
                            type="text"
                            icon={<DownloadOutlined style={{ color: '#64748b', fontSize: '15px' }} />}
                            title="Download (right-click for PNG / SVG / CSV)"
                            onClick={() => handleDownloadGraph('pdf')}
                            style={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '8px',
                                background: 'rgba(248, 250, 252, 0.95)',
                                border: '1px solid #e2e8f0',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                cursor: 'pointer'
                            }}
                        />
                    </Dropdown>
                )}
            </div>
            )}

            {title && (
                <h4 style={{
                    margin: '0 0 12px 0',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#1a1a2e'
                }}>
                    {title}
                </h4>
            )}

            {/* Range selector for charts with many X-axis values - hidden in compact/dashboard mode */}
            {!compact && supportsRangeSelection && data.length > 15 && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 12,
                    padding: '8px 12px',
                    flexWrap: 'wrap',
                }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Showing {rangeStart} to {rangeEnd} of {data.length} items
                    </Text>
                    <Space size="small" wrap>
                        <Space size={4}>
                            <Text type="secondary" style={{ fontSize: 12 }}>From:</Text>
                            <InputNumber
                                size="small"
                                min={1}
                                max={data.length}
                                value={tempRangeStart}
                                onChange={(val) => setTempRangeStart(val)}
                                style={{ width: 65 }}
                            />
                        </Space>
                        <Space size={4}>
                            <Text type="secondary" style={{ fontSize: 12 }}>To:</Text>
                            <InputNumber
                                size="small"
                                min={1}
                                max={data.length}
                                value={tempRangeEnd}
                                onChange={(val) => setTempRangeEnd(val)}
                                style={{ width: 65 }}
                            />
                        </Space>
                        <Button
                            size="small"
                            type="primary"
                            className="cip-btn-submit"
                            onClick={handleSearchClick}
                        >
                            Search
                        </Button>
                    </Space>
                </div>
            )}
            {renderChart()}
        </div>
    );
});

export default GraphView;
